import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import {
  formatValueLabel,
  formatPercentageLabel,
  formatNumberWithCommas,
  calculateCategoryTotals,
  convertToPercentage,
  sortSeriesByTotal,
  buildConfigurableTooltipFormatter,
  buildConfigurableAxisLabel,
} from './../../utils/chart.utils';
import {
  getSeriesColor,
  ColorConfig,
} from './../../utils/color.utils';
import { formatStat } from '../../utils/format.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'generic-stackbar-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, CapitalCasePipe],
  templateUrl: './generic-stackbar-chart.html',
  styleUrl: './generic-stackbar-chart.scss',
})
export class GenericStackbarChart implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  protected myChart!: echarts.ECharts;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];
  protected enableQuarterYearAxis = false;
  protected xAxisQuarters: string[] = [];
  protected xAxisYears: string[] = [];
  protected xAxisYearsRaw: string[] = [];
  protected xAxisPrimaryLabels: string[] = [];
  protected xAxisSecondaryLabels: string[] = [];
  private isCtrlPressed = false;

  // ===== Wrapper — handles filter subscription, click events, legend sync, color sync, highlight =====
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  private configService = inject(ConfigService);
  private globalFilterService = inject(GlobalFilterService);
  private chartFilterHandler = inject(ChartFilterHandlerService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: any;

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
    });

    setTimeout(() => this.loadAndRenderChart(initialFilters ?? undefined), 0);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId || '');
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.initChart(this.lastChartData);
    });

    this.addKeyboardListeners();
  }

  // ========================
  // DATA LOADING
  // ========================

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    const hasFilters = filters?.requestParams && Object.keys(filters.requestParams).length > 0;
    const useApi = this.data?.apiUrl && hasFilters;

    if (useApi) {
      this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
        next: (res) => this.initChart(res?.data || res),
        error: () => {
          if (this.data?.data) {
            this.initChart(this.data.data);
          } else {
            this.myChart.hideLoading();
          }
        },
      });
    } else if (this.data?.data) {
      this.initChart(this.data.data);
    } else if (this.data?.apiUrl) {
      this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
        next: (res) => this.initChart(res?.data || res),
        error: () => this.myChart.hideLoading(),
      });
    } else {
      this.myChart.hideLoading();
    }

    this.addResizeListener();
  }

  // ========================
  // CHART INITIALIZATION (chart-specific rendering logic stays here)
  // ========================

  protected initChart(chartData: any): void {
    if (!chartData) return;

    this.lastChartData = chartData;

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const config = this.data?.chartConfig || {};
    const showLegend = config.showLegend !== false;
    const legendPosition = config.legendPosition || 'top';
    const showDataLabels = config.showDataLabels !== false;
    const dataLabelPosition = config.dataLabelPosition || 'inside';
    const showTotalLabels = config.showTotalLabels !== false;
    const colorMap = config.colorMap || {};
    const barWidth = config.barWidth;
    const showYAxisLabels = config.showYAxisLabels !== false;
    const stackType = config?.stackType || 'normal';
    this.enableQuarterYearAxis = config.enableQuarterYearAxis === true;
    const stackOrder = config?.stackOrder || 'desc';
    const seriesOrder: string[] = config?.seriesOrder || [];
    const positionColors = config.topNColorsByStackPosition || [];

    const categoryFields: string[] = this.data?.categoryFields || ['quarter', 'year'];
    const categoryDelimiter: string = this.data?.categoryDelimiter ?? ' ';
    const valueField: string = this.data?.valueField || 'value';
    const lineSeriesDataKey = this.data?.lineSeriesDataKey;
    const lineSeriesKey = this.data?.lineSeriesKey;
    const lineSeriesValueField = this.data?.lineSeriesValueField || 'value';

    const buildCategoryString = (item: any): string =>
      categoryFields.map((field) => item[field] ?? '').join(categoryDelimiter);

    let categories: string[] = [];
    let seriesData: any[] = [];
    let lineSeriesData: any[] = [];

    // 1. GENERIC FLAT ARRAY FORMAT
    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey;

    if (dataKey && chartData[dataKey] && Array.isArray(chartData[dataKey])) {
      const flatData = chartData[dataKey];
      this.wrapper.setSourceData(flatData, categoryFields[0], this.data?.seriesKey || 'series');
      categories = Array.from(new Set(flatData.map((d: any) => buildCategoryString(d))));
      const uniqueSeriesNames = Array.from(new Set(flatData.map((d: any) => d[seriesKey] || 'Unknown')));

      seriesData = uniqueSeriesNames.map((name: any) => {
        const seriesPoints = categories.map((cat) => {
          const point = flatData.find((d: any) => d[seriesKey] === name && buildCategoryString(d) === cat);
          return point ? { ...point, value: point[valueField] ?? 0 } : { value: 0 };
        });
        return { name, type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: seriesPoints };
      });
    }
    // 2. Dictionary Format
    else if (!chartData.category && !chartData.series) {
      const seriesNames = Object.keys(chartData);
      if (seriesNames.length > 0) {
        const firstSeriesData = chartData[seriesNames[0]];
        if (Array.isArray(firstSeriesData)) {
          categories = firstSeriesData.map((item: any) => buildCategoryString(item));
        }
        seriesData = seriesNames.map((name) => ({
          name,
          type: 'bar',
          stack: 'total',
          emphasis: { focus: 'series' },
          data: Array.isArray(chartData[name])
            ? chartData[name].map((d: any) => ({ ...d, value: d?.[valueField] ?? 0 }))
            : [],
        }));
      }
    }
    // 3. Standard Format (category + series)
    else if (chartData.category && chartData.series) {
      categories = chartData.category;
      seriesData = chartData.series.map((s: any) => ({
        name: s.name,
        type: 'bar',
        stack: 'total',
        emphasis: { focus: 'series' },
        data: s.data,
      }));
    }

    // 4. Line series (optional)
    if (lineSeriesDataKey && chartData[lineSeriesDataKey] && Array.isArray(chartData[lineSeriesDataKey])) {
      const flatLineData = chartData[lineSeriesDataKey];
      if (categories.length === 0) {
        categories = Array.from(new Set(flatLineData.map((d: any) => buildCategoryString(d))));
      }

      if (this.data?.useSingleLineSeries === true) {
        const lineSeriesPoints = categories.map((cat) => {
          const point = flatLineData.find((d: any) => buildCategoryString(d) === cat);
          return point ? { ...point, value: point[lineSeriesValueField] ?? 0 } : { value: 0 };
        });
        lineSeriesData = [{
          name: this.data?.lineSeriesName || 'Trend',
          type: 'line', smooth: true, yAxisIndex: 1, emphasis: { focus: 'series' }, data: lineSeriesPoints,
        }];
      } else {
        const uniqueLineSeriesNames = Array.from(new Set(flatLineData.map((d: any) => d[lineSeriesKey] || 'Unknown')));
        lineSeriesData = uniqueLineSeriesNames.map((name: any) => {
          const seriesPoints = categories.map((cat) => {
            const point = flatLineData.find((d: any) => d[lineSeriesKey] === name && buildCategoryString(d) === cat);
            return point ? { ...point, value: point[lineSeriesValueField] ?? 0 } : { value: 0 };
          });
          return { name, type: 'line', smooth: true, yAxisIndex: 1, emphasis: { focus: 'series' }, data: seriesPoints };
        });
      }
    }

    // Sort series
    let sortedSeriesData: any[];
    if (seriesOrder.length > 0) {
      sortedSeriesData = [...seriesData].sort((a, b) => {
        const indexA = seriesOrder.indexOf(a.name);
        const indexB = seriesOrder.indexOf(b.name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });
    } else {
      sortedSeriesData = sortSeriesByTotal(seriesData, stackOrder === 'desc' ? 'asc' : 'desc');
    }

    this.allSeriesNames = sortedSeriesData.map((s) => s.name).concat(lineSeriesData.map((s) => s.name));
    this.allCategories = categories;

    if (this.enableQuarterYearAxis) {
      this.xAxisQuarters = categories.map((cat: string) => {
        const qMatch = String(cat).match(/Q\s*([1-4])/i);
        return qMatch ? `Q${qMatch[1]}` : cat;
      });
      this.xAxisYearsRaw = categories.map((cat: string) => {
        const yMatch = String(cat).match(/(19|20)\d{2}/);
        return yMatch ? yMatch[0] : '';
      });
      this.xAxisYears = this.buildCenteredAxisLabels(this.xAxisYearsRaw);
    } else {
      this.xAxisQuarters = [];
      this.xAxisYears = [];
      this.xAxisYearsRaw = [];
    }

    const totals = calculateCategoryTotals(categories, seriesData);
    const displaySeriesData = stackType === 'percentage'
      ? convertToPercentage(sortedSeriesData, totals)
      : sortedSeriesData;

    const colorConfig: ColorConfig = { colorMap, topNColorsByStackPosition: positionColors };

    // Build series with labels and colors — uses wrapper for color consistency
    const seriesWithLabels = displaySeriesData.map((s, index) => {
      let seriesColor = getSeriesColor(index, s.name, colorConfig);
      seriesColor = this.wrapper.applyColorConsistency(s.name, seriesColor) || seriesColor;

      const seriesConfig: any = {
        ...s,
        ...(barWidth ? { barWidth } : {}),
        ...(seriesColor ? { itemStyle: { color: seriesColor } } : {}),
      };

      if (showDataLabels) {
        seriesConfig.label = {
          show: true,
          position: dataLabelPosition,
          formatter: (params: any) =>
            stackType === 'percentage' ? formatPercentageLabel(params.value) : formatValueLabel(params.value),
          fontSize: 10,
          color: dataLabelPosition === 'inside' ? '#fff' : (theme.chart?.textColor || '#333'),
        };
      }

      return seriesConfig;
    });

    const allSeries: any[] = [...seriesWithLabels];

    // Add line series
    if (lineSeriesData.length > 0) {
      const lineSeriesWithLabels = lineSeriesData.map((s, index) => {
        const seriesColor = getSeriesColor(index, s.name, colorConfig);
        const seriesConfig: any = {
          ...s, smooth: true, yAxisIndex: 1, symbol: 'circle', symbolSize: 6,
          lineStyle: { width: 2, ...(seriesColor ? { color: seriesColor } : {}) },
          ...(seriesColor ? { itemStyle: { color: seriesColor } } : {}),
        };
        if (showDataLabels) {
          seriesConfig.label = { show: true, position: 'top', formatter: (params: any) => formatValueLabel(params.value), fontSize: 10, color: theme.chart?.textColor || '#333' };
        }
        return seriesConfig;
      });
      allSeries.push(...lineSeriesWithLabels);
    }

    // Add total series
    if (showTotalLabels) {
      allSeries.push({
        name: 'Total', type: 'bar', stack: 'total', data: totals.map(() => 0),
        label: {
          show: true, position: 'top', distance: 8,
          formatter: (params: any) => stackType === 'percentage' ? '100%' : formatValueLabel(totals[params.dataIndex]),
          fontSize: 11, fontWeight: 'bold', color: theme.chart?.textColor || '#333',
        },
        itemStyle: { color: 'transparent' }, emphasis: { itemStyle: { color: 'transparent' } },
      });
    }

    // Legend
    const allSeriesNamesForLegend = [...sortedSeriesData.map((s) => s.name), ...lineSeriesData.map((s) => s.name)];
    const legendConfig: any = {
      show: showLegend, data: allSeriesNamesForLegend.reverse(), type: 'scroll', itemGap: 16, padding: [5, 10, 10, 10],
    };
    if (legendPosition === 'top') legendConfig.top = 0;
    else if (legendPosition === 'bottom') legendConfig.bottom = 0;
    else if (legendPosition === 'left') { legendConfig.left = 0; legendConfig.orient = 'vertical'; }
    else if (legendPosition === 'right') { legendConfig.right = 0; legendConfig.orient = 'vertical'; }

    // X-axis
    const yearAxisOffset = Number(config.yearAxisOffset ?? 22);
    const xAxisConfig = this.enableQuarterYearAxis
      ? [
          { type: 'category', data: this.xAxisQuarters, triggerEvent: true, axisLabel: { clickable: true }, axisTick: { alignWithLabel: true } },
          { type: 'category', data: this.xAxisYears, position: 'bottom', offset: yearAxisOffset, triggerEvent: true, axisLabel: { clickable: true, interval: 0 }, axisTick: { show: false }, axisLine: { show: true } },
        ] as any
      : { type: 'category', data: categories, triggerEvent: true, axisLabel: { clickable: true } } as any;

    const leftAxisLabelConfig = buildConfigurableAxisLabel(config?.yAxis);
    const rightAxisLabelConfig = buildConfigurableAxisLabel(config?.yAxisRight);
    const leftAxisUseFormatStat = config?.yAxis?.useFormatStat;

    const leftAxisFormatter =
      stackType === 'percentage'
        ? (leftAxisLabelConfig?.formatter || '{value}%')
        : (
            leftAxisLabelConfig?.formatter
            || (leftAxisUseFormatStat === false ? '{value}' : ((value: number) => formatStat(value)))
          );

    // Y-axis
    const yAxisConfig = lineSeriesData.length > 0
      ? [
          {
            type: 'value',
            name: config?.yAxis?.name || 'Sales',
            position: 'left',
            splitLine: { show: false },
            axisLabel: {
              show: leftAxisLabelConfig?.show ?? showYAxisLabels,
              formatter: leftAxisFormatter,
            },
            ...(stackType === 'percentage' ? { max: 100 } : {}),
          },
          {
            type: 'value',
            name: config?.yAxisRight?.name || 'Gross Margin %',
            position: 'right',
            splitLine: { show: false },
            axisLabel: {
              show: rightAxisLabelConfig?.show ?? true,
              formatter: rightAxisLabelConfig?.formatter || '{value}%',
            },
            min: config.rightAxisMin ?? 40,
            max: 100,
          },
        ]
      : {
          type: 'value',
          splitLine: { show: false },
          axisLabel: {
            show: leftAxisLabelConfig?.show ?? showYAxisLabels,
            formatter: leftAxisFormatter,
          },
          ...(stackType === 'percentage' ? { max: 100 } : {}),
        };

    const option: any = {
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        borderWidth: 1,
        padding: [10, 14],
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          const configurableFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
          if (configurableFormatter) return configurableFormatter;
          return (params: any) => {
            if (params.seriesName === 'Total') return '';
            const total = totals[params.dataIndex];
            if (stackType === 'percentage') {
              const originalSeries = seriesData.find((s) => s.name === params.seriesName);
              const originalValue = originalSeries?.data[params.dataIndex] || 0;
              return `<div style="text-align:left; line-height: 1.8;"><div><b>Year Quarter:</b> ${params.name}</div><div><b>Updated Segment:</b> ${params.seriesName}</div><div><b>Percentage:</b> ${params.value.toFixed(1)}%</div><div><b>Value:</b> ${formatNumberWithCommas(originalValue)}</div><div><b>Total:</b> ${formatNumberWithCommas(total)}</div></div>`;
            }
            return `<div style="text-align:left; line-height: 1.8;"><div><b>Year Quarter:</b> ${params.name}</div><div><b>Updated Segment:</b> ${params.seriesName}</div><div><b>Sum of Total Sales:</b> ${formatNumberWithCommas(params.value)}</div><div><b>Total:</b> ${formatNumberWithCommas(total)}</div></div>`;
          };
        })(),
      },
      legend: legendConfig,
      grid: {
        left: '3%', right: lineSeriesData.length > 0 ? '8%' : '4%',
        top: legendPosition === 'top' && showLegend ? '12%' : '5%',
        bottom: config.gridBottom ?? 0, containLabel: true,
      },
      xAxis: xAxisConfig,
      yAxis: yAxisConfig,
      series: allSeries,
    };

    if (this.myChart) {
      this.wrapper.currentlySelectedIndices = [];
      this.myChart.setOption(option, true);
      this.myChart.hideLoading();

      // Let wrapper re-apply visual state and wire click/legend events
      this.wrapper.afterRender();

      // OVERRIDE: GenericStackbar has custom click handling for dual-axis and updateFilterSelection.
      // We re-wire click after wrapper.afterRender() to use our custom handlers.
      this.setupCustomClickEvent();
    }
  }

  // ========================
  // CUSTOM CLICK HANDLING (GenericStackbar-specific logic that differs from the generic wrapper)
  // ========================

  protected setupCustomClickEvent(): void {
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params?.componentType === 'legend') {
        params?.event?.event?.preventDefault?.();
        params?.event?.event?.stopPropagation?.();
        return;
      }

      if (params?.componentType !== 'series' && params?.componentType !== 'xAxis') return;
      if (params?.componentType === 'series' && !Number.isInteger(params?.dataIndex)) return;

      if (params?.componentType === 'xAxis' && (this.data?.chartConfig?.syncXaxisLabels ?? true)) {
        this.handleXAxisLabelClick(params);
        return;
      }

      this.updateFilterSelection(params);
    });
  }

  // ========================
  // X-AXIS LABEL CLICK (dual-axis quarter/year logic)
  // ========================

  protected handleXAxisLabelClick(params: any): void {
    if (!this.data?.chartConfig?.enableQuarterYearAxis) {
      this.wrapper.handleLabelClick(params.value);
      return;
    }

    const axisIndex = params?.axisIndex;
    const idx = params?.valueIndex ?? params?.dataIndex;
    if (idx == null || idx < 0) return;
    const rawValue = String(params?.value ?? '').trim();
    const isYearValue = /^(19|20)\d{2}$/.test(rawValue);
    const isQuarterValue = /^Q\s*[1-4]$/i.test(rawValue);

    // axis 0 = quarter row
    if (axisIndex === 0 || (axisIndex == null && isQuarterValue)) {
      const fullCategory = this.allCategories[idx];
      this.wrapper.handleLabelClick(fullCategory);
      return;
    }

    // axis 1 = year row
    if (axisIndex === 1 || (axisIndex == null && isYearValue)) {
      this.handleYearAxisClick(rawValue, idx, params);
      return;
    }

    // Fallback for ambiguous
    if (isYearValue) {
      this.handleYearAxisClick(rawValue, idx, params);
    }
  }

  protected handleYearAxisClick(rawValue: string, idx: number, params: any): void {
    const clickedYear = this.xAxisYearsRaw[idx] || this.xAxisYears[idx] || rawValue;
    if (!clickedYear) return;

    const nativeEvent = params.event?.event || params.event;
    const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;

    const selectedYears = Array.from(new Set(
      (this.wrapper.selectedCategories || []).map((c: string) => this.extractYearFromCategory(c)).filter(Boolean),
    ));

    let nextYears: string[];
    if (ctrlPressed) {
      nextYears = selectedYears.includes(clickedYear) ? selectedYears.filter((y) => y !== clickedYear) : [...selectedYears, clickedYear];
    } else {
      nextYears = selectedYears.length === 1 && selectedYears[0] === clickedYear ? [] : [clickedYear];
    }

    const nextCategories = nextYears.length === 0 ? [] : this.allCategories.filter((c: string) => nextYears.includes(this.extractYearFromCategory(c)));

    this.wrapper.currentlySelectedIndices = [];
    this.wrapper.selectLegendsByIndices([]);

    if (nextCategories.length === 0) {
      this.wrapper.selectedCategories = [];
      this.wrapper.resetCategoryHighlight();
      this.updateXAxisFiltersFromSelectedCategories();
      this.wrapper.broadcastCategoryFilter([]);
    } else {
      this.wrapper.selectedCategories = nextCategories;
      this.wrapper.highlightCategories(nextCategories);
      this.updateXAxisFiltersFromSelectedCategories();
      this.wrapper.broadcastCategoryFilter(nextCategories);
    }
  }

  // ========================
  // CUSTOM updateFilterSelection (GenericStackbar-specific payload construction)
  // ========================

  protected updateFilterSelection(params: any): void {
    if (!this.data?.filterKey) return;

    const filterKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : this.data.filterKey.split(',').map((k: string) => k.trim());

    const payload: any = {};
    filterKeys.forEach((key: string) => {
      if (params.data && typeof params.data === 'object' && key in params.data) { payload[key] = params.data[key]; return; }
      if (key === this.data.seriesKey) { payload[key] = params.seriesName; return; }
      if (key === this.data.xAxis || (this.data.categoryFields && this.data.categoryFields.includes(key))) { payload[key] = params.name; return; }
      if (key === 'series' || key === 'segment') { payload[key] = params.seriesName; return; }
      if (key === 'year' || key === 'quarter' || key === 'qtr' || key === 'category') { payload[key] = params.name; return; }
    });

    if (Object.keys(payload).length === 0) return;
    this.chartFilterHandler.updateFilterSelection(this.data, payload);
  }

  // ========================
  // X-AXIS FILTER STATE (GenericStackbar-specific year/quarter logic)
  // ========================

  protected updateXAxisFiltersFromSelectedCategories(): void {
    const enableQtr = !!this.data?.chartConfig?.enableQuarterYearAxis;
    const listener = this.data?.filterListener || this.data?.filterListner;
    const seriesKey = this.data?.seriesKey || 'segment';
    const legendSeriesValues = this.wrapper.currentlySelectedIndices
      .map((i: number) => this.allSeriesNames[i])
      .filter(Boolean);

    if (!this.wrapper.selectedCategories?.length) {
      const keysToClear = enableQtr ? ['year', 'qtr'] : ['year'];
      keysToClear.forEach((key, index) => {
        const isLast = index === keysToClear.length - 1;
        if (isLast) this.globalFilterService.resetFilterSelection(key, false, listener);
        else this.globalFilterService.deleteKey(key, false);
      });
      return;
    }

    const yearValues = this.wrapper.selectedCategories.map((cat: string) => this.extractYearFromCategory(cat)).filter(Boolean);
    if (!yearValues.length) return;

    if (!enableQtr) {
      const payload: any = { year: yearValues.length === 1 ? yearValues[0] : yearValues };
      const cfg: any = { ...this.data, filterKey: ['year'] };
      if (legendSeriesValues.length > 0) {
        payload[seriesKey] = legendSeriesValues.length === 1 ? legendSeriesValues[0] : legendSeriesValues;
        cfg.filterKey = [seriesKey, 'year'];
      }
      this.chartFilterHandler.updateFilterSelection(cfg, payload);
      return;
    }

    const qtrValues = this.wrapper.selectedCategories.map((cat: string) => this.extractQuarterFromCategory(cat)).filter(Boolean);
    if (!qtrValues.length) return;

    const payload: any = { year: yearValues.length === 1 ? yearValues[0] : yearValues, qtr: qtrValues.length === 1 ? qtrValues[0] : qtrValues };
    const cfg: any = { ...this.data, filterKey: ['year', 'qtr'] };
    if (legendSeriesValues.length > 0) {
      payload[seriesKey] = legendSeriesValues.length === 1 ? legendSeriesValues[0] : legendSeriesValues;
      cfg.filterKey = [seriesKey, 'year', 'qtr'];
    }
    this.chartFilterHandler.updateFilterSelection(cfg, payload);
  }

  // ========================
  // HELPERS
  // ========================

  protected extractYearFromCategory(cat: string): string {
    const m = String(cat).match(/(19|20)\d{2}/);
    return m ? m[0] : '';
  }

  protected extractQuarterFromCategory(cat: string): string {
    const str = String(cat ?? '').toUpperCase();
    const qMatch = str.match(/\bQ\s*([1-4])\b/);
    if (qMatch) return `Q${qMatch[1]}`;
    const quarterWordMatch = str.match(/\bQUARTER\s*([1-4])\b/);
    if (quarterWordMatch) return `Q${quarterWordMatch[1]}`;
    const qtrMatch = str.match(/\bQTR\s*([1-4])\b/);
    if (qtrMatch) return `Q${qtrMatch[1]}`;
    return '';
  }

  protected buildCenteredAxisLabels(values: string[]): string[] {
    const centered = new Array(values.length).fill('');
    for (let start = 0; start < values.length;) {
      const value = values[start];
      let end = start;
      while (end + 1 < values.length && values[end + 1] === value) end++;
      if (value) {
        const midpoint = Math.floor((start + end) / 2);
        centered[midpoint] = value;
      }
      start = end + 1;
    }
    return centered;
  }

  // ========================
  // KEYBOARD & RESIZE
  // ========================

  protected addKeyboardListeners(): void {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleWindowBlur);
  }

  private handleKeyDown = (event: KeyboardEvent): void => { if (event.key === 'Control') this.isCtrlPressed = true; };
  private handleKeyUp = (event: KeyboardEvent): void => { if (event.key === 'Control') this.isCtrlPressed = false; };
  private handleWindowBlur = (): void => { this.isCtrlPressed = false; };

  private resizeObserver: ResizeObserver | null = null;

  protected addResizeListener(): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (chartDom) {
      this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
      this.resizeObserver.observe(chartDom);
    }
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => { this.myChart?.resize(); };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleWindowBlur);
    this.myChart?.dispose();
  }
}
